import { jsx as _jsx } from "react/jsx-runtime";
import { ActiveTabLabel } from "@/components/Search/index";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Modal } from "antd";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { Subjects, SubjectsWithString } from "src/api/product";
import { getSearchFilter, getSearchProducts, SearchQueryKey, sortKeys, SortType } from "src/api/search";
const FILTER_ORDERING = {
    [SearchQueryKey.SOLVOOKPASSES]: 1,
    [SearchQueryKey.RELATEDBOOKS]: 2,
    [SearchQueryKey.UNITS]: 3,
    [SearchQueryKey.DATATYPES]: 4,
    [SearchQueryKey.AUTHORS]: 5,
    [SearchQueryKey.TICKETS]: 6
};
export const useSearchPageData = (param)=>{
    let { filterParams , selectedFilterList  } = param;
    const router = useRouter();
    const [bottom, inView] = useInView();
    // 개발 환경에서 mockFreeTrial 쿼리 파라미터로 무료체험 표시 테스트
    // ?mockFreeTrial=eligible → 첫 3개 아이템에 freeTrial: { eligible: true } 추가
    // ?mockFreeTrial=not-eligible → 첫 3개 아이템에 freeTrial: { eligible: false } 추가
    const mockFreeTrial = "production" === "development" && router.isReady ? router.query.mockFreeTrial : undefined;
    useEffect(()=>{
        if (inView) {
            hasNextPage !== false && fetchNextPage() && mixpanelEvent("Load Item list", {
                Position: "검색결과리스트",
                Volume: 20
            });
        }
    }, [
        inView
    ]);
    const { data: filterResponse  } = useQuery([
        "getSearchFilter",
        filterParams.q,
        filterParams.subject,
        filterParams.productType
    ], ()=>getSearchFilter({
            available: filterParams.available,
            q: filterParams.q,
            subject: filterParams.subject,
            productType: filterParams.productType
        }), {
        keepPreviousData: true,
        enabled: !!filterParams.q
    });
    //연관교재 필터: 미사용 필터
    const { data: keywordFilterResponse  } = useQuery([
        "getSearchKeywordFilter",
        filterParams.q,
        filterParams.subject,
        filterParams.productType
    ], ()=>getSearchFilter({
            q: filterParams.q,
            subject: filterParams.subject,
            productType: filterParams.productType,
            available: filterParams.available
        }), {
        keepPreviousData: true,
        enabled: !!filterParams.q
    });
    const LIMIT = 20;
    const { data: listData , fetchNextPage , hasNextPage , isFetchingNextPage , isLoading , isSuccess  } = useInfiniteQuery([
        "searchList",
        filterParams
    ], (param)=>{
        let { pageParam =0  } = param;
        return getSearchProducts({
            ...filterParams,
            productType: filterParams.productType,
            offset: pageParam,
            limit: LIMIT
        });
    }, {
        getNextPageParam: (lastPage)=>{
            if (lastPage.pagination.offset + LIMIT >= (lastPage.pagination.total || 0)) return undefined;
            return lastPage.pagination.offset + LIMIT;
        },
        onError: (error)=>{
            ;
            Modal.error({
                title: "상품리스트를 불러오지 못했습니다. ",
                content: error.message
            });
        },
        cacheTime: 5 * 60 * 1000,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 1
    });
    // Mock freeTrial 데이터 적용 (개발용)
    const transformedListData = useMemo(()=>{
        if (!listData || !mockFreeTrial) return listData;
        const eligible = mockFreeTrial === "eligible";
        return {
            ...listData,
            pages: listData.pages.map((page, pageIndex)=>({
                    ...page,
                    data: page.data.map((item, itemIndex)=>{
                        // 첫 페이지의 첫 3개 아이템에만 freeTrial 추가
                        if (pageIndex === 0 && itemIndex < 3) {
                            return {
                                ...item,
                                freeTrial: {
                                    eligible
                                }
                            };
                        }
                        return item;
                    })
                }))
        };
    }, [
        listData,
        mockFreeTrial
    ]);
    const newFilterList = useMemo(()=>{
        if (!filterResponse) return {};
        let newFilter = {
            ...filterResponse[filterParams.productType]
        };
        delete newFilter[SearchQueryKey.CONTENTTYPES];
        delete newFilter[SearchQueryKey.EDITABLES];
        // 필터 순서를 변경하는 요구사항으로 인해, 필터 순서를 변경합니다. 객체의 경우 순서가 보장되지 않기 때문에 map을 사용하였습니다.
        const orderingFilter = Object.entries(newFilter).sort((param, param1)=>{
            let [aKey] = param, [bKey] = param1;
            return FILTER_ORDERING[aKey] - FILTER_ORDERING[bKey];
        });
        const map = new Map(orderingFilter);
        return Object.fromEntries(map);
    }, [
        filterResponse
    ]);
    const keywordFilterList = useMemo(()=>{
        if (keywordFilterResponse && keywordFilterResponse[filterParams.productType] && filterParams.subject === Subjects.EN) {
            return keywordFilterResponse[filterParams.productType][SearchQueryKey.RELATEDBOOKS] || [];
        }
        return [];
    }, [
        keywordFilterResponse,
        filterParams.productType
    ]);
    const hasFilterItems = useMemo(()=>{
        return filterResponse && Object.keys(newFilterList).length > 0;
    }, [
        filterResponse
    ]);
    const subjectTabList = useMemo(()=>{
        return [
            Subjects.EN,
            Subjects.KO,
            Subjects.MA,
            Subjects.SC,
            Subjects.SO
        ].map((subject)=>{
            return {
                key: subject,
                label: /*#__PURE__*/ _jsx(ActiveTabLabel, {
                    children: SubjectsWithString[subject]
                })
            };
        });
    }, []);
    const sortList = useMemo(()=>{
        return sortKeys.map((sort)=>{
            return {
                value: sort,
                label: /*#__PURE__*/ _jsx("div", {
                    className: "select-label",
                    children: SortType[sort]
                })
            };
        });
    }, []);
    const totalCount = useMemo(()=>{
        if (!listData) return 0;
        return listData.pages[0].pagination.total;
    }, [
        listData
    ]);
    const emptyStatus = useMemo(()=>{
        if (isSuccess && selectedFilterList.length > 0 && totalCount === 0) return "FILTER";
        if (!listData || isSuccess && totalCount === 0) {
            //믹스패널용 조건문
            if (isSuccess && totalCount === 0) {
                mixpanelEvent("No Search Results Found", {
                    position: "handout",
                    keyword: filterParams.q,
                    subject: filterParams.subject
                });
            }
            return "ITEM";
        }
        return null;
    }, [
        isSuccess,
        selectedFilterList,
        totalCount,
        listData
    ]);
    // 편집가능 필터 추후 사용을 위해 주석처리 하였습니다.
    // const hasEditableFilter: boolean = useMemo(() => {
    //   return (
    //     filterResponse &&
    //     filterResponse[filterParams.productType]?.editables &&
    //     filterResponse[filterParams.productType][SearchQueryKey.EDITABLES].includes(true)
    //   )
    // }, [filterResponse])
    const hasSolvookPassesFilter = useMemo(()=>{
        var _filterResponse_filterParams_productType;
        return filterResponse && ((_filterResponse_filterParams_productType = filterResponse[filterParams.productType]) === null || _filterResponse_filterParams_productType === void 0 ? void 0 : _filterResponse_filterParams_productType.solvookPasses) && filterResponse[filterParams.productType][SearchQueryKey.SOLVOOKPASSES].length > 0;
    }, [
        filterResponse
    ]);
    return {
        subjectTabList,
        sortList,
        listData: transformedListData,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        newFilterList,
        keywordFilterList,
        hasFilterItems,
        totalCount,
        emptyStatus,
        bottom,
        filterResponse,
        hasSolvookPassesFilter
    };
};
